/* eslint-disable no-unused-expressions */

/* eslint-disable no-sequences */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { axiosClient } from "../../axios";
import { showErrorToast, showSuccessToast } from "../../utils/toastUtils";
import { API_ENDPOINTS } from "../../constants/apiEndpoints";
import { compressImgUrl } from "../../context/compressor";

// Async Thunks
export const registerUser = createAsyncThunk(
  "auth/registerUser",
  async (values, thunkAPI) => {
    const payload = {
      first_name: values.firstName,
      last_name: values.lastName,
      email: values.email,
      username: values.username,
      phone_number: values.phoneNumber,
      password: values.password,
      account_type: values.accountType,
      gender_id: values.gender,
      ...(values.user_id && { user_id: values.user_id }),
    };

    try {
      const response = await axiosClient.post(API_ENDPOINTS.REGISTER, payload);
      const { data } = response;

      // Store user data in localStorage
      localStorage.setItem("token", data.access);
      localStorage.setItem("refresh_token", data.refresh);
      localStorage.setItem("user", JSON.stringify(data.user));
      localStorage.setItem("account", JSON.stringify(data.account));

      values?.resetForm();
      values.moveToLanding();
      showSuccessToast("Account registration successful.");
      return data;
    } catch (error) {
      window.navigator.vibrate([200, 100, 200]);
      showErrorToast(error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const loginUser = createAsyncThunk(
  "auth/loginUser",
  async (values, thunkAPI) => {
    try {
      const payload = values.first_login
        ? {
            first_login: 1,
            username: values.phone_number.toUpperCase(),
          }
        : {
            username: values.username,
            password: values.password,
          };

      const response = await axiosClient.post(API_ENDPOINTS.LOGIN, payload);

      if (values.first_login) {
        handleFirstTimeLogin(response.data, values);
      } else {
        handleNormalLogin(response.data, values);
      }

      return response.data;
    } catch (error) {
      window.navigator.vibrate([200, 100, 200]);
      showErrorToast(error.message);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const fetchUserByToken = createAsyncThunk(
  "auth/fetchUserByToken",
  async (_, thunkAPI) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axiosClient.get(API_ENDPOINTS.GET_USER, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const { data: user } = response;
      handleUserPhotoCompression(user);

      return user;
    } catch (error) {
      showErrorToast(error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const verifyOTP = createAsyncThunk(
  "auth/verifyOTP",
  async (values, thunkAPI) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axiosClient.post(
        API_ENDPOINTS.VALIDATE_OTP,
        { otp: values.otp },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      localStorage.setItem("account", JSON.stringify(response.data.account_info));
      localStorage.setItem("accountType", JSON.stringify(response.data.account_type));

      window.navigator.vibrate([200, 100, 200]);
      values.moveToLanding();
      return response.data;
    } catch (error) {
      window.navigator.vibrate([200, 100, 200]);
      localStorage.removeItem("token");
      showErrorToast(error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const editUserProfile = createAsyncThunk(
  "auth/editProfile",
  async (values, thunkAPI) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axiosClient.post(
        API_ENDPOINTS.EDIT_PROFILE,
        values.data,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      localStorage.setItem("user", JSON.stringify(response.data));
      showSuccessToast("Profile updated successfully");
      return response.data;
    } catch (error) {
      showErrorToast(error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const changePasswordProfile = createAsyncThunk(
  "auth/changePasswordProfile",
  async (values, thunkAPI) => {
    try {
      values.passwordFeed({ pwordLoading: true });
      const token = localStorage.getItem("token");

      const response = await axiosClient.post(
        API_ENDPOINTS.CHANGE_PASSWORD,
        { password: values.password },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      values.passwordFeed({ pwordLoading: false });
      localStorage.setItem("user", JSON.stringify(response.data));
      showSuccessToast(response.data.message);
      return response.data;
    } catch (error) {
      values.passwordFeed({ pwordLoading: false });
      window.navigator.vibrate([200, 100, 200]);
      showErrorToast(error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const sendResetLink = createAsyncThunk(
  "auth/sendResetLink",
  async (values, thunkAPI) => {
    try {
      const response = await axiosClient.post(API_ENDPOINTS.CONFIRM_ACCOUNT, values.data);
      values.goToResetPage(response.data);
      return response.data;
    } catch (error) {
      
      if(error.response){
        const errorObject={...error};
        console.log(errorObject, "error object");
        const { response :{data}} = errorObject;
       
        showErrorToast(data);
      }else{
         showErrorToast(error);
      }
     
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const saveNewPassword = createAsyncThunk(
  "auth/saveNewPassword",
  async (values, thunkAPI) => {
    try {
      const response = await axiosClient.post(API_ENDPOINTS.RESET_PASSWORD, values.data);
      showSuccessToast("Password changed successfully.... redirecting");
      setTimeout(() => values.goToLogin(), 2000);
      return response.data;
    } catch (error) {
      window.navigator.vibrate([200, 100, 200]);
      showErrorToast(error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const checkExistingMember = createAsyncThunk(
  "auth/checkexisting",
  async (values, thunkAPI) => {
    try {
      const response = await axiosClient.post(API_ENDPOINTS.EXISTING_MEMBER, {
        member_number: values.memberNo,
      });
      values.moveToExistingPasswordReset();
      return response.data;
    } catch (error) {
      showErrorToast(error);
      values.moveToExistingPasswordReset();
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const addExistingMember = createAsyncThunk(
  "auth/addexisting",
  async (values, thunkAPI) => {
    try {
      const response = await axiosClient.post(API_ENDPOINTS.EXISTING_MEMBER, {
        otp: values.otp,
        password: values.password,
      });
      values.handleSuccess(response.data);
      return response.data;
    } catch (error) {
      values.handleSuccess(error.message);
      showErrorToast(error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

// Helper Functions
const handleFirstTimeLogin = (data, values) => {
  const { user, account_type } = data;

  if (Array.isArray(user)) {
    showErrorToast(
      "Information not found for first time login. Try logging in here instead"
    );
    values.handleBack();
    return;
  }

  if (user?.personal_info?.first_login === true) {
    if (!user?.personal_info?.phone_number) {
      showErrorToast(
        `Dear ${user?.first_name ?? "member"}, Looks like your phone number is missing in the system. kindly contact support`
      );
      return;
    }

    localStorage.setItem("user", JSON.stringify(user));
    localStorage.setItem("accountType", JSON.stringify(account_type));
    values.showFirstLoginOTP("");
  } else {
    showErrorToast(
      "Looks like this is your first time login. Try logging in here instead"
    );
    values.handleBack();
  }
};

const handleNormalLogin = (data, values) => {
  if ("detail" in data) {
    throw new Error(data.detail);
  }

  const { access, refresh } = data;
  localStorage.setItem("token", access);
  localStorage.setItem("refresh_token", refresh);
  values.moveToOtp();
};

const handleUserPhotoCompression = (user) => {
  if (user?.id < 500 && user?.personal_info?.photo_url) {
    compressImgUrl(
      user.personal_info.photo_url,
      user,
      (newImage, userObj, error = false) => {
        const userPayload = {
          ...userObj,
          lastLogin: Date.now(),
          personal_info: {
            ...userObj.personal_info,
            photo_url: error ? null : newImage,
          },
        };
        localStorage.setItem("user", JSON.stringify(userPayload));
      }
    );
  } else {
    localStorage.setItem(
      "user",
      JSON.stringify({ ...user, lastLogin: Date.now() })
    );
  }
};

// Slice Definition
export const AuthSlice = createSlice({
  name: "auth",
  initialState: {
    isFetching: false,
    isSuccess: false,
    isError: false,
    errorMessage: null,
    personalInfo: {},
    user: {},
    account: {},
    accountType: {},
    resetData: {},
  },
  reducers: {
    clearState: (state) => {
      state.isError = false;
      state.isSuccess = false;
      state.isFetching = false;
    },
    logoutStateClear: (state) => {
      state.isError = false;
      state.isSuccess = false;
      state.isFetching = false;
      state.user = {};
      state.account = {};
      state.personalInfo = {};
    },
    getCurrentUser: (state) => {
      const user_ = JSON.parse(localStorage.getItem("user"));
      const storedUser = user_?.user ?? user_;
      if (storedUser) {
        state.user = storedUser;
      }
    },
  },
  extraReducers: (builder) => {
    // Generic builder helpers
    const setPending = (state) => {
      state.isFetching = true;
    };

    const setRejected = (state, { payload }) => {
      state.isFetching = false;
      state.isError = true;
      state.errorMessage = payload;
    };

    const setFulfilled = (state, { payload }) => {
      state.isFetching = false;
      state.isSuccess = true;
      state.errorMessage = "";

      if (payload?.user) state.user = payload.user;
      if (payload?.personal_info) state.personalInfo = payload.personal_info;
      if (payload?.account) state.account = payload.account;
      if (payload?.account_type) state.accountType = payload.account_type;
      if (payload?.account_info) state.account = payload.account_info;
    };

    // Register
    builder
      .addCase(registerUser.pending, setPending)
      .addCase(registerUser.rejected, setRejected)
      .addCase(registerUser.fulfilled, setFulfilled)

      // Login
      .addCase(loginUser.pending, setPending)
      .addCase(loginUser.rejected, setRejected)
      .addCase(loginUser.fulfilled, setFulfilled)

      // Fetch User
      .addCase(fetchUserByToken.pending, setPending)
      .addCase(fetchUserByToken.rejected, setRejected)
      .addCase(fetchUserByToken.fulfilled, setFulfilled)

      // Verify OTP
      .addCase(verifyOTP.pending, setPending)
      .addCase(verifyOTP.rejected, setRejected)
      .addCase(verifyOTP.fulfilled, setFulfilled)

      // Edit Profile
      .addCase(editUserProfile.pending, setPending)
      .addCase(editUserProfile.rejected, setRejected)
      .addCase(editUserProfile.fulfilled, setFulfilled)

      // Change Password
      .addCase(changePasswordProfile.pending, setPending)
      .addCase(changePasswordProfile.rejected, setRejected)
      .addCase(changePasswordProfile.fulfilled, setFulfilled)

      // Send Reset Link
      .addCase(sendResetLink.pending, setPending)
      .addCase(sendResetLink.rejected, setRejected)
      .addCase(sendResetLink.fulfilled, (state, { payload }) => {
        setFulfilled(state, { payload });
        state.resetData = payload;
      })

      // Save New Password
      .addCase(saveNewPassword.pending, setPending)
      .addCase(saveNewPassword.rejected, setRejected)
      .addCase(saveNewPassword.fulfilled, setFulfilled)

      // Check Existing Member
      .addCase(checkExistingMember.pending, setPending)
      .addCase(checkExistingMember.rejected, setRejected)
      .addCase(checkExistingMember.fulfilled, setFulfilled)

      // Add Existing Member
      .addCase(addExistingMember.pending, setPending)
      .addCase(addExistingMember.rejected, setRejected)
      .addCase(addExistingMember.fulfilled, setFulfilled);
  },
});

export const { clearState, logoutStateClear, getCurrentUser } = AuthSlice.actions;
export const authSelector = (state) => state.auth;
